import { el } from "../core/dom";
import { part } from "../appearance/parts";
import {
  ACCEPT_ALL_BUTTON_ID,
  ACCEPT_SELECTED_BUTTON_ID,
  DECLINE_BUTTON_ID,
  DEFAULT_ACCEPT_ALL_BUTTON_TEXT,
  DEFAULT_ACCEPT_SELECTED_BUTTON_TEXT,
  DEFAULT_DECLINE_BUTTON_TEXT,
} from "../constants";
import { ASSISTED_CONFIG, ASSISTED_IDS, FOCUS_KEY_ATTRIBUTE, FOCUS_KEYS } from "./constants";
import { checkIcon, clockIcon, lockIcon, mailIcon, phoneIcon } from "./icons";
import { areAllRequiredElementsChecked, isContactValid, isOtpComplete } from "./selection";
import { assistedStyles } from "./styles";
import type { AssistedPrimaryAction, AssistedViewContext } from "./types";

const renderContactField = (ctx: AssistedViewContext): HTMLElement => {
  const { state, handlers } = ctx;
  const locked = state.otpSent || state.isSendingOtp;
  if (state.verifyMethod === "mobile") {
    return el(
      "div",
      { className: "redacto-assisted-phone-field", style: assistedStyles.phoneField },
      el("span", { style: assistedStyles.phonePrefix }, ASSISTED_CONFIG.dialCode),
      el("input", {
        id: ASSISTED_IDS.mobileInput,
        type: "tel",
        inputmode: "numeric",
        autocomplete: "tel-national",
        className: "redacto-assisted-phone-input",
        placeholder: "98765 43210",
        value: state.mobile,
        disabled: locked,
        onInput: (event: Event) => handlers.onMobileInput((event.target as HTMLInputElement).value),
      })
    );
  }
  return el(
    "div",
    { className: "redacto-assisted-phone-field", style: assistedStyles.phoneField },
    el("input", {
      id: ASSISTED_IDS.emailInput,
      type: "email",
      inputmode: "email",
      autocomplete: "email",
      className: "redacto-assisted-phone-input",
      placeholder: "you@example.com",
      value: state.email,
      disabled: locked,
      onInput: (event: Event) => handlers.onEmailInput((event.target as HTMLInputElement).value),
    })
  );
};

export const otpBoxStyle = (ctx: AssistedViewContext, digit: string, index: number) => ({
  borderColor: digit ? ctx.primaryColor : "",
  backgroundColor: digit ? ctx.accentTint : "",
  marginLeft: index === 3 ? "8px" : "",
});

export const renderResend = (ctx: AssistedViewContext): HTMLElement => {
  const { state, ts, handlers, primaryColor } = ctx;
  if (state.resendCountdown > 0) {
    return el(
      "p",
      { id: ASSISTED_IDS.resend, style: assistedStyles.resendText },
      clockIcon("#667085"),
      ts("resendIn", { seconds: state.resendCountdown })
    );
  }
  return el(
    "p",
    { id: ASSISTED_IDS.resend, style: assistedStyles.resendText },
    el(
      "button",
      {
        type: "button",
        className: "redacto-assisted-link",
        style: { color: primaryColor },
        disabled: state.isSendingOtp,
        [FOCUS_KEY_ATTRIBUTE]: FOCUS_KEYS.resend,
        onClick: () => handlers.onResend(),
      },
      state.isSendingOtp ? ts("sending") : ts("resendOtp")
    )
  );
};

const renderOtpEntry = (ctx: AssistedViewContext): HTMLElement => {
  const { state, ts, handlers } = ctx;
  const boxes = state.otpDigits.map((digit, index) =>
    el("input", {
      id: `${ASSISTED_IDS.otpInputPrefix}${index}`,
      type: "text",
      inputmode: "numeric",
      autocomplete: "one-time-code",
      maxlength: "1",
      className: `redacto-assisted-otp-input${state.otpError ? " redacto-assisted-otp-error" : ""}`,
      style: otpBoxStyle(ctx, digit, index),
      value: digit,
      disabled: state.isVerifying,
      "aria-label": ts("otpDigit", { index: index + 1 }),
      onInput: (event: Event) => handlers.onOtpInput(index, (event.target as HTMLInputElement).value),
      onKeydown: (event: KeyboardEvent) => handlers.onOtpKeydown(index, event),
      onPaste: (event: ClipboardEvent) => handlers.onOtpPaste(event),
    })
  );

  return el(
    "div",
    { style: { display: "flex", flexDirection: "column", gap: "12px" } },
    el(
      "p",
      { style: assistedStyles.centerLabel },
      ts("enterNDigitOtp", { count: ASSISTED_CONFIG.otpLength })
    ),
    el("div", { style: assistedStyles.otpRow }, ...boxes),
    state.otpError ? el("p", { role: "alert", style: assistedStyles.fieldError }, state.otpError) : null,
    renderResend(ctx)
  );
};

export const renderVerifyPanel = (ctx: AssistedViewContext): HTMLElement => {
  const { state, ts, handlers, primaryColor } = ctx;
  const isEmail = state.verifyMethod === "email";
  const linksLocked = state.isVerifying || state.isSendingOtp;

  const labelRow = el(
    "div",
    { style: assistedStyles.fieldLabelRow },
    el(
      "label",
      { for: isEmail ? ASSISTED_IDS.emailInput : ASSISTED_IDS.mobileInput, style: assistedStyles.fieldLabel },
      isEmail ? mailIcon("#475467") : phoneIcon("#475467"),
      isEmail ? ts("emailAddress") : ts("mobileNumber")
    ),
    state.otpSent
      ? null
      : el(
          "button",
          {
            type: "button",
            className: "redacto-assisted-link",
            style: { color: primaryColor },
            disabled: state.isSendingOtp,
            [FOCUS_KEY_ATTRIBUTE]: FOCUS_KEYS.switchMethod,
            onClick: () => handlers.onSwitchMethod(),
          },
          isEmail ? phoneIcon(primaryColor) : mailIcon(primaryColor),
          isEmail ? ts("useMobileInstead") : ts("useEmailInstead")
        )
  );

  const status = state.otpSent
    ? el(
        "div",
        { style: assistedStyles.otpStatusRow },
        el(
          "span",
          { style: assistedStyles.otpSentText },
          checkIcon("#10B981"),
          `${ts("otpSentTo")} ${isEmail ? state.email : `${ASSISTED_CONFIG.dialCode} ${state.mobile}`}`
        ),
        el(
          "button",
          {
            type: "button",
            className: "redacto-assisted-link",
            style: { color: primaryColor },
            disabled: linksLocked,
            [FOCUS_KEY_ATTRIBUTE]: FOCUS_KEYS.changeContact,
            onClick: () => handlers.onChangeContact(),
          },
          isEmail ? ts("changeEmail") : ts("changeMobile")
        )
      )
    : el("p", { style: assistedStyles.helperText }, isEmail ? ts("otpHelperEmail") : ts("otpHelper"));

  return el(
    "div",
    {
      id: ASSISTED_IDS.verifyPanel,
      className: "redacto-assisted-reveal",
      style: assistedStyles.verifyPanel,
    },
    el(
      "div",
      { style: assistedStyles.verifyPanelHeader },
      el("span", { style: assistedStyles.verifyPanelTitle }, lockIcon(primaryColor), ts("verifyWithOtp")),
      el(
        "button",
        {
          type: "button",
          className: "redacto-assisted-link",
          style: { color: primaryColor },
          disabled: linksLocked,
          [FOCUS_KEY_ATTRIBUTE]: FOCUS_KEYS.editSelections,
          onClick: () => handlers.onEditSelections(),
        },
        ts("editSelections")
      )
    ),
    el(
      "div",
      { style: { display: "flex", flexDirection: "column", gap: "10px" } },
      labelRow,
      renderContactField(ctx),
      status,
      state.createError
        ? el("p", { role: "alert", style: assistedStyles.fieldError }, state.createError)
        : null,
      state.otpSent ? renderOtpEntry(ctx) : null
    )
  );
};

export const primaryAction = (ctx: AssistedViewContext): AssistedPrimaryAction => {
  const { state, content, t, ts, handlers } = ctx;
  if (state.step === "verify" && !state.otpSent) {
    return {
      label: state.isSendingOtp ? ts("sending") : ts("sendOtp"),
      disabled: !isContactValid(state.verifyMethod, state.mobile, state.email) || state.isSendingOtp,
      action: handlers.onGetOtp,
    };
  }
  if (state.step === "verify") {
    return {
      label: state.isVerifying ? ts("verifying") : ts("confirmOtp"),
      disabled: !isOtpComplete(state.otpDigits) || state.isVerifying,
      action: handlers.onConfirmOtp,
    };
  }
  return {
    label: t("confirm_button_text", content.confirm_button_text || DEFAULT_ACCEPT_SELECTED_BUTTON_TEXT),
    disabled: !areAllRequiredElementsChecked(ctx.displayedPurposes, state.selectedDataElements),
    action: handlers.onAcceptSelected,
  };
};

export const renderFooter = (ctx: AssistedViewContext): HTMLElement => {
  const { state, content, t, handlers, isMobile, primaryColor, styles, look } = ctx;
  const isVerify = state.step === "verify";
  const buttonBase = { ...styles.button, ...ctx.responsive.button };
  const width = isMobile ? assistedStyles.stackButton : assistedStyles.rowButton;
  const primaryState = primaryAction(ctx);

  const primary = el(
    "button",
    {
      type: "button",
      id: ACCEPT_SELECTED_BUTTON_ID,
      ...(isVerify ? {} : part("accept-selected")),
      className: "redacto-assisted-primary-btn",
      style: isVerify
        ? { ...buttonBase, ...styles.acceptButton, backgroundColor: primaryColor }
        : { ...buttonBase, ...styles.cancelButton, ...width, ...look.acceptSelected },
      disabled: primaryState.disabled,
      [FOCUS_KEY_ATTRIBUTE]: FOCUS_KEYS.primary,
      onClick: () => primaryState.action(),
    },
    primaryState.label
  );

  const decline = el(
    "button",
    {
      type: "button",
      id: DECLINE_BUTTON_ID,
      ...part("decline"),
      style: {
        ...buttonBase,
        ...styles.cancelButton,
        ...(isVerify ? {} : width),
        ...(isVerify || isMobile ? {} : assistedStyles.declineSpacer),
        ...look.decline,
      },
      [FOCUS_KEY_ATTRIBUTE]: FOCUS_KEYS.decline,
      onClick: () => handlers.onDecline(),
    },
    t("decline_button_text", content.decline_button_text || DEFAULT_DECLINE_BUTTON_TEXT)
  );

  if (isVerify) {
    return el(
      "div",
      { ...part("footer"), style: { ...styles.bottomSection, flexDirection: isMobile ? "column" : "row" } },
      primary,
      decline
    );
  }

  const acceptAll = el(
    "button",
    {
      type: "button",
      id: ACCEPT_ALL_BUTTON_ID,
      ...part("accept-all"),
      style: { ...buttonBase, ...styles.acceptButton, ...width, ...look.acceptAll },
      [FOCUS_KEY_ATTRIBUTE]: FOCUS_KEYS.acceptAll,
      onClick: () => handlers.onAcceptAll(),
    },
    t("accept_all_button_text", content.accept_all_button_text || DEFAULT_ACCEPT_ALL_BUTTON_TEXT)
  );

  return isMobile
    ? el("div", { ...part("footer"), style: styles.actionButtonStack }, acceptAll, primary, decline)
    : el("div", { ...part("footer"), style: styles.actionButtonRow }, decline, primary, acceptAll);
};
